import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import TimerIcon from "@material-ui/icons/Timer";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { PageHeader, StatCard, StatusBadge, EmptyState, MainContainer, Section } from "../../components/ui";
import { getTokens, brandAlpha } from "../../styles/tokens";
import { getBackendUrl } from "../../config";
import { formatDuration } from "../../components/SlaChip";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    scroll: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      ...theme.scrollbarStyles,
    },
    page: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(3),
      maxWidth: 1280,
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      paddingBottom: theme.spacing(2),
    },
    pageHeader: { paddingTop: 0, paddingBottom: 0 },
    // 1 → 2 → 3+2 → 5 colunas. Na linha de 5 a coluna nunca fica menor que o
    // conteúdo: rótulo e valor ("618h 37m") aparecem inteiros, sem reticências.
    statGrid: {
      display: "grid",
      gap: 16,
      gridTemplateColumns: "minmax(0, 1fr)",
      [theme.breakpoints.up("sm")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        "& > :last-child": { gridColumn: "1 / -1" },
      },
      [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
        "& > :nth-child(n)": { gridColumn: "span 2" },
        "& > :nth-child(n+4)": { gridColumn: "span 3" },
      },
      "@media (min-width: 1300px)": {
        gridTemplateColumns: "repeat(5, minmax(max-content, 1fr))",
        "& > :nth-child(n)": { gridColumn: "auto" },
        "& > :nth-child(n+4)": { gridColumn: "auto" },
      },
    },
    sectionHint: {
      display: "block",
      marginTop: 2,
      fontSize: 13,
      fontWeight: 400,
      color: t.textSecondary,
    },
    updatedAt: {
      fontSize: 12,
      color: t.textMuted,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },
    tableWrap: {
      overflowX: "auto",
    },
    table: {
      minWidth: 720,
      "& th, & td": { whiteSpace: "nowrap" },
      // alinha a primeira/última coluna com o título da seção (20px)
      "& th:first-child, & td:first-child": { paddingLeft: 20 },
      "& th:last-child, & td:last-child": { paddingRight: 20 },
      "& tbody tr:last-child td": { borderBottom: 0 },
    },
    queueName: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 500,
    },
    queueDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flex: "none",
    },
    num: { fontWeight: 600 },
    numMuted: { color: t.textMuted },
    numWarning: { color: t.semantic.warning, fontWeight: 600 },
    numError: { color: t.semantic.error, fontWeight: 600 },
    meter: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 160,
    },
    meterTrack: {
      flex: 1,
      height: 8,
      borderRadius: 4,
      overflow: "hidden",
      backgroundColor: t.mode === "dark" ? t.line : t.muted,
    },
    meterBar: {
      height: "100%",
      borderRadius: 4,
      transition: "width 300ms ease",
    },
    meterValue: {
      width: 44,
      textAlign: "right",
      fontSize: 13,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
    },
    emptyDivider: { borderBottom: `1px solid ${t.line}` },
    agentGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))",
      gap: 12,
    },
    // dentro do cartão da seção: só borda, sem sombra própria
    agentCard: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      padding: 14,
      minWidth: 0,
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
    },
    agentCardBreached: {
      borderColor: brandAlpha(t.semantic.error, 0.5),
    },
    agentHead: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 0,
    },
    agentAvatar: {
      width: 40,
      height: 40,
      fontWeight: 600,
    },
    agentInfo: {
      minWidth: 0,
      flex: 1,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    agentName: {
      fontSize: 14,
      fontWeight: 600,
      color: t.text,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    agentStatusRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      fontSize: 12,
      color: t.textMuted,
    },
    agentStats: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 8,
    },
    agentStat: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      padding: "8px 10px",
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      minWidth: 0,
    },
    agentStatLabel: {
      fontSize: 11,
      fontWeight: 500,
      color: t.textSecondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    agentStatValue: {
      fontSize: 16,
      fontWeight: 600,
      color: t.text,
      fontVariantNumeric: "tabular-nums",
    },
    loadingRow: {
      display: "flex",
      justifyContent: "center",
      padding: 32,
    },
  };
});

const formatAgo = (iso, now = Date.now()) => {
  if (!iso) return i18n.t("supervision.time.never");
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return i18n.t("supervision.time.never");
  const sec = Math.max(0, Math.floor((now - ms) / 1000));
  if (sec < 60) return i18n.t("supervision.time.now");
  const min = Math.floor(sec / 60);
  if (min < 60) return i18n.t("supervision.time.minutes", { count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return i18n.t("supervision.time.hours", { count: h });
  return i18n.t("supervision.time.days", { count: Math.floor(h / 24) });
};

const pctColor = (t, pct) => {
  if (pct == null) return t.textMuted;
  if (pct >= 90) return t.semantic.success;
  if (pct >= 70) return t.semantic.warning;
  return t.semantic.error;
};

const EMPTY_OVERVIEW = {
  queues: [],
  agents: [],
  totals: { waiting: 0, oldestWaitingSec: 0, open: 0, breached: 0, online: 0 },
};

const Supervision = () => {
  const classes = useStyles();
  const theme = useTheme();
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;
  const backendUrl = getBackendUrl();

  const allowed = user?.profile === "admin" || user?.allowRealTime === "enabled";

  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [queuesWithSla, setQueuesWithSla] = useState(null); // null = ainda não carregado
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [tick, setTick] = useState(Date.now());
  const debounceRef = useRef(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const fetchOverview = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const { data } = await api.get("/supervision/overview");
      if (!mountedRef.current) return;
      setOverview({
        queues: Array.isArray(data?.queues) ? data.queues : [],
        agents: Array.isArray(data?.agents) ? data.agents : [],
        totals: { ...EMPTY_OVERVIEW.totals, ...(data?.totals || {}) },
      });
      setUpdatedAt(Date.now());
    } catch (err) {
      if (mountedRef.current) toastError(err);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const fetchQueues = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      if (!mountedRef.current) return;
      const list = Array.isArray(data) ? data : [];
      setQueuesWithSla(
        list.filter((q) => Number(q.slaFirstResponseMinutes) > 0 || Number(q.slaResolutionMinutes) > 0).length
      );
    } catch (err) {
      if (mountedRef.current) setQueuesWithSla(0);
    }
  }, []);

  // carga inicial + polling 30 s + relógio local (para "visto há X")
  useEffect(() => {
    mountedRef.current = true;
    if (!allowed) return undefined;
    fetchOverview();
    fetchQueues();
    const poll = setInterval(fetchOverview, 30000);
    const clock = setInterval(() => setTick(Date.now()), 15000);
    return () => {
      mountedRef.current = false;
      clearInterval(poll);
      clearInterval(clock);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [allowed, fetchOverview, fetchQueues]);

  // socket: refetch com debounce 2 s
  useEffect(() => {
    if (!allowed || !socket || typeof socket.on !== "function" || !companyId) return undefined;
    const schedule = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        fetchOverview();
      }, 2000);
    };
    const events = [`company-${companyId}-ticket`, `company-${companyId}-sla`, "user:online", "user:offline"];
    events.forEach((ev) => socket.on(ev, schedule));
    return () => {
      events.forEach((ev) => socket.off(ev, schedule));
    };
  }, [allowed, socket, companyId, fetchOverview]);

  const totals = overview.totals;

  const cards = useMemo(
    () => [
      { label: i18n.t("supervision.cards.waiting"), value: totals.waiting || 0, icon: <HourglassEmptyIcon />, tone: totals.waiting > 0 ? "warning" : "neutral" },
      { label: i18n.t("supervision.cards.oldestWaiting"), value: formatDuration(totals.oldestWaitingSec || 0), icon: <AccessTimeIcon />, tone: "info" },
      { label: i18n.t("supervision.cards.open"), value: totals.open || 0, icon: <ForumOutlinedIcon />, tone: "brand" },
      { label: i18n.t("supervision.cards.breached"), value: totals.breached || 0, icon: <ReportProblemOutlinedIcon />, tone: totals.breached > 0 ? "error" : "success" },
      { label: i18n.t("supervision.cards.online"), value: `${totals.online || 0}/${overview.agents.length}`, icon: <PeopleOutlineIcon />, tone: "brand" },
    ],
    [totals, overview.agents.length]
  );

  if (!allowed) {
    return <ForbiddenPage />;
  }

  const showQueuesEmpty = queuesWithSla === 0;

  const avatarSrc = (agent) =>
    agent?.profileImage ? `${backendUrl}/public/company${companyId}/user/${agent.profileImage}` : undefined;

  // título da seção com a frase de apoio logo abaixo
  const sectionHeading = (title, hint) => (
    <>
      {title}
      {hint && <span className={classes.sectionHint}>{hint}</span>}
    </>
  );

  const queuesEmptyState = (
    <EmptyState
      icon={<TimerIcon />}
      title={i18n.t("supervision.queues.emptyTitle")}
      message={i18n.t("supervision.queues.emptyMessage")}
      action={
        user?.profile === "admin" ? (
          <Button color="primary" variant="contained" onClick={() => history.push("/queues")}>
            {i18n.t("supervision.queues.emptyAction")}
          </Button>
        ) : null
      }
    />
  );

  const initials = (name) =>
    String(name || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join("")
      .toUpperCase();

  return (
    <MainContainer>
      <div className={classes.scroll}>
        <div className={classes.page}>
          <PageHeader
            className={classes.pageHeader}
            title={i18n.t("supervision.title")}
            subtitle={i18n.t("supervision.subtitle")}
            actions={
              <>
                {updatedAt && (
                  <span className={classes.updatedAt}>
                    <TimerIcon style={{ fontSize: 14 }} />
                    {i18n.t("supervision.updatedAt", { time: formatAgo(updatedAt, tick) })}
                  </span>
                )}
                <Button variant="outlined" onClick={fetchOverview} startIcon={<RefreshIcon />}>
                  {i18n.t("supervision.refresh")}
                </Button>
              </>
            }
          />

          {/* Cards de topo */}
          <div className={classes.statGrid}>
            {cards.map((c) => (
              <StatCard key={c.label} label={c.label} value={c.value} icon={c.icon} tone={c.tone} />
            ))}
          </div>

          {/* Filas */}
          <Section
            flush
            title={sectionHeading(i18n.t("supervision.queues.title"), i18n.t("supervision.queues.hint"))}
          >
            {loading && overview.queues.length === 0 ? (
              <div className={classes.loadingRow}>
                <CircularProgress size={24} />
              </div>
            ) : showQueuesEmpty && overview.queues.length === 0 ? (
              queuesEmptyState
            ) : (
              <>
                {showQueuesEmpty && <div className={classes.emptyDivider}>{queuesEmptyState}</div>}
                <div className={classes.tableWrap}>
                  <Table size="small" className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{i18n.t("supervision.queues.queue")}</TableCell>
                        <TableCell align="right">{i18n.t("supervision.queues.waiting")}</TableCell>
                        <TableCell align="right">{i18n.t("supervision.queues.oldest")}</TableCell>
                        <TableCell align="right">{i18n.t("supervision.queues.open")}</TableCell>
                        <TableCell align="right">{i18n.t("supervision.queues.warning")}</TableCell>
                        <TableCell align="right">{i18n.t("supervision.queues.breached")}</TableCell>
                        <TableCell>{i18n.t("supervision.queues.slaToday")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overview.queues.map((q) => {
                        const pct = q.slaPctToday == null ? null : Math.max(0, Math.min(100, Number(q.slaPctToday) || 0));
                        const color = pctColor(t, pct);
                        return (
                          <TableRow key={q.id}>
                            <TableCell>
                              <span className={classes.queueName}>
                                <span className={classes.queueDot} style={{ backgroundColor: q.color || t.textMuted }} />
                                {q.name}
                              </span>
                            </TableCell>
                            <TableCell align="right" className={q.waiting > 0 ? classes.num : classes.numMuted}>
                              {q.waiting || 0}
                            </TableCell>
                            <TableCell align="right" className={q.waiting > 0 ? classes.num : classes.numMuted}>
                              {q.waiting > 0 ? formatDuration(q.oldestWaitingSec || 0) : "—"}
                            </TableCell>
                            <TableCell align="right">{q.open || 0}</TableCell>
                            <TableCell align="right" className={q.warning > 0 ? classes.numWarning : classes.numMuted}>
                              {q.warning || 0}
                            </TableCell>
                            <TableCell align="right" className={q.breached > 0 ? classes.numError : classes.numMuted}>
                              {q.breached || 0}
                            </TableCell>
                            <TableCell>
                              {pct == null ? (
                                <span className={classes.numMuted}>{i18n.t("supervision.queues.noSla")}</span>
                              ) : (
                                <div className={classes.meter}>
                                  <div className={classes.meterTrack}>
                                    <div className={classes.meterBar} style={{ width: `${pct}%`, backgroundColor: color }} />
                                  </div>
                                  <span className={classes.meterValue} style={{ color }}>
                                    {Math.round(pct)}%
                                  </span>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </Section>

          {/* Atendentes */}
          <Section title={sectionHeading(i18n.t("supervision.agents.title"), i18n.t("supervision.agents.hint"))}>
            {overview.agents.length === 0 ? (
              loading ? (
                <div className={classes.loadingRow}>
                  <CircularProgress size={24} />
                </div>
              ) : (
                <EmptyState icon={<PeopleOutlineIcon />} title={i18n.t("supervision.agents.empty")} />
              )
            ) : (
              <div className={classes.agentGrid}>
                {overview.agents.map((a) => (
                  <div className={`${classes.agentCard} ${a.breached > 0 ? classes.agentCardBreached : ""}`} key={a.id}>
                    <div className={classes.agentHead}>
                      <Avatar src={avatarSrc(a)} className={classes.agentAvatar}>
                        {initials(a.name)}
                      </Avatar>
                      <div className={classes.agentInfo}>
                        <Tooltip title={a.name || ""}>
                          <span className={classes.agentName}>{a.name}</span>
                        </Tooltip>
                        <div className={classes.agentStatusRow}>
                          <StatusBadge
                            size="sm"
                            status={a.online ? "online" : "offline"}
                            label={i18n.t(a.online ? "supervision.agents.online" : "supervision.agents.offline")}
                          />
                          {!a.online && (
                            <span>{i18n.t("supervision.agents.lastSeen", { ago: formatAgo(a.lastSeen, tick) })}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={classes.agentStats}>
                      <div className={classes.agentStat}>
                        <span className={classes.agentStatLabel}>{i18n.t("supervision.agents.openTickets")}</span>
                        <span className={classes.agentStatValue}>{a.openTickets || 0}</span>
                      </div>
                      <div className={classes.agentStat}>
                        <span className={classes.agentStatLabel}>{i18n.t("supervision.agents.breached")}</span>
                        <span
                          className={classes.agentStatValue}
                          style={{ color: a.breached > 0 ? t.semantic.error : undefined }}
                        >
                          {a.breached || 0}
                        </span>
                      </div>
                      <div className={classes.agentStat}>
                        <span className={classes.agentStatLabel}>{i18n.t("supervision.agents.avgFirstResponse")}</span>
                        <span className={classes.agentStatValue}>
                          {a.avgFirstResponseSec == null ? "—" : formatDuration(a.avgFirstResponseSec)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </MainContainer>
  );
};

export default Supervision;
